// src/components/LessonPreview.js
import React, { useEffect, useState } from "react";
import AddLocationIcon from "@mui/icons-material/AddLocation";
import { buildFullHTML } from "../utils/htmlUtils";
import { injectOverlaysIntoBody } from "../utils/htmlUtils";

export function LessonPreview({
  iframeRef,
  headContent,
  bodyContent,
  baseUrl,            // e.g. lesson.content_gcp_path
  editVisualMode,
  onVisualSelect,
  onTextSelect,       // (selectedText: string, range: Range) => void
  pendingPlacement,   // id or truthy flag
  children,           // optional overlay/UI
  styles,              // pass in your common styles object
  byRel
}) {
    
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  
  const placementRef   = React.useRef(pendingPlacement);
  const textSelectRef  = React.useRef(onTextSelect);
  useEffect(() => { placementRef.current  = pendingPlacement; }, [pendingPlacement]);
  useEffect(() => { textSelectRef.current = onTextSelect;    }, [onTextSelect]);

  function setOverlayInteractivity(doc, active) {
    doc.querySelectorAll(".me-visual-overlay").forEach(ov => {
      ov.style.pointerEvents = active ? "auto" : "none";
      // new – outline instead of border so layout never shifts
      ov.style.outline       = active ? "3px dashed transparent" : "0";
    });
  }

  const preparedBody = React.useMemo(
    () => injectOverlaysIntoBody(bodyContent, byRel),
    [bodyContent, byRel]
  );

  // 1) Render HTML + block unwanted navigation
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    doc.open();
    doc.write(
      buildFullHTML(headContent, preparedBody, {
        isPreview: true,
        baseUrl
      })
    );
    doc.close();

    setOverlayInteractivity(doc, editVisualMode);


    /* 4 – inject CSS once */
    if (!doc.getElementById("me-preview-css")) {
      const style = doc.createElement("style");
      style.id = "me-preview-css";
      style.textContent = `
        /* overlays sit absolutely on top of iframe and never alter flow */
        [data-me-preview-wrapper] { position:relative; }
        .me-visual-overlay     {
          position:absolute; inset:0;
          outline:3px dashed transparent; /* replaces old border */
          border:0; box-sizing:border-box;
        }
        .me-visual-overlay:hover,
        .me-visual-overlay.selected {
          outline-color:#42a5f5;
          cursor:pointer;
        }
        .me-visual-overlay:hover::after {
          content:"Click to edit";
          position:absolute;top:4px;right:8px;
          background:#42a5f5;color:#fff;font-size:10px;
          padding:2px 4px;border-radius:3px;pointer-events:none;
        }`;
      doc.head.appendChild(style);
    }

    /* 5 – listeners */
    const clickHandler = (e) => {
      const ov = e.target.closest(".me-visual-overlay");
      if (!ov || !editVisualMode) return;
      e.preventDefault(); e.stopPropagation();

      const id       = ov.dataset.visualId;
      const iframeEl = ov.previousElementSibling;   // the real <iframe>
      const wrapper  = ov.closest("div");           // pass wrapper (relative)
      onVisualSelect({ id, wrapper, iframeEl });
    };

    const handleSelection = () => {
      if (placementRef.current) return;
      const sel = doc.getSelection();
      if (sel && !sel.isCollapsed) {
        const range = sel.getRangeAt(0);
        textSelectRef.current(sel.toString(), range);
      }
    };

    const blockNav = e => {
      const a = e.target.closest("a");
      if (a && a.target !== "_blank" && !a.dataset.allowPopup) {
        e.preventDefault(); e.stopPropagation();
      }
    };

    doc.addEventListener("click", clickHandler, true);
    doc.addEventListener("mouseup", handleSelection);
    doc.addEventListener("keyup", handleSelection);
    doc.addEventListener("click", blockNav, true);

    return () => {
      doc.removeEventListener("click", clickHandler, true);      
      doc.removeEventListener("mouseup", handleSelection);
      doc.removeEventListener("keyup", handleSelection);
      doc.removeEventListener("click", blockNav, true);
    };
  }, [headContent, preparedBody, baseUrl, editVisualMode]);

  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
  
    const handleMouseMove = e => {
      // only do anything if we're in placement mode
      if (!pendingPlacement) return;
  
      const { left, top } = iframeRef.current.getBoundingClientRect();
      const absX = left + e.clientX;
      const absY = top  + e.clientY;
      setCursorPos({ x: absX, y: absY });
    };
  
    doc.addEventListener("mousemove", handleMouseMove);
    return () => {
      doc.removeEventListener("mousemove", handleMouseMove);
    };
  }, [pendingPlacement, iframeRef, editVisualMode]);
  
  useEffect(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    setOverlayInteractivity(doc, editVisualMode);
  }, [editVisualMode]);
  
  return (
    <div style={styles.iframeWrapper}>
      {pendingPlacement && (
        <AddLocationIcon
          sx={{
            position: "fixed",
            top: cursorPos.y,
            left: cursorPos.x,
            fontSize: 32,
            zIndex: 10000,
            pointerEvents: "none"
          }}
        />
      )}
      <iframe
        ref={iframeRef}
        title="Lesson Preview"
        style={styles.iframe}
      />
      {children}
    </div>
  );
}
